import { useTranslation } from 'react-i18next';

import { CFSectionProps } from '../../tixologist/CustomFieldsSection/types';
import { PunchupCustomQuestionTile } from 'features/common/components/Punchup/composites/tiles/PunchupCustomQuestionTile';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { PunchupInlineEditor } from 'features/common/components/Punchup/composites/editors/PunchupInlineEditor';
import { CustomFieldForm } from '../../tixologist/CustomFieldsSection/CustomFieldForm/CustomFieldForm';
import { useGetCustomFields } from 'features/common/components/CustomFieldsComponent/useGetCustomFields';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { getCustomFieldName } from 'utils/utils';

// Drag-to-reorder was removed: the API persisted the dragged `row` but never read it back (neither the
// Go query nor the frontend sorts by it), so the order silently reset on reload — a confusing no-op.
// The list renders in the order the API returns. (The native dark-theme reorder sub-page is unrelated.)
export const CustomFieldsSection = ({ eventMode: _eventMode }: CFSectionProps) => {
	const { t } = useTranslation();
	const { customFields } = useGetCustomFields();
	const { event } = useGetEvent();
	// Tint each bound ticket-type chip with its colour (resolved by name from the shared source) — the
	// same colour the tables, pool member chips, and tile rails use.
	const { resolveByName } = useTicketTypeColors();
	const fields = customFields ?? [];
	// When a question targets every ticket type, show one "All ticket types" chip instead of a chip
	// per type — both clearer and shorter than listing all of them.
	const totalTicketTypes = event?.ticketTypes?.length ?? 0;

	// Same copy for every tile — built once (stable reference keeps the memoised tiles from re-rendering).
	const questionHelp = (
		<PunchupHelpButton
			mainText={t(
				'customFields-tile-help',
				'A question asked at checkout. Each answer shows up on the Attendee List, where you can export the whole set.'
			)}
		/>
	);

	return (
		<PunchupInlineEditor
			addLabel={t('dashboard-add-green')}
			renderView={({ startEdit }) =>
				fields.length > 0 ? (
					<div className="punchupTileColumn">
						{fields.map((field) => {
							const boundTypes = field.ticket_types ?? [];
							const coversAll = totalTicketTypes > 0 && boundTypes.length >= totalTicketTypes;
							const ticketTypeNames = coversAll
								? [t('customFields-all-ticket-types', 'All ticket types')]
								: boundTypes.map((tt) => tt.name);
							// The "All ticket types" meta-chip has no single type colour → neutral grey.
							const ticketTypeColors = coversAll ? [null] : boundTypes.map((tt) => resolveByName(tt.name));
							return (
								<PunchupCustomQuestionTile
									key={field.ID}
									question={getCustomFieldName(field.name)}
									answererLabel={
										field.only_buyer ? t('customFields-buyerOnly-green') : t('customFields-eachAttendee-green')
									}
									required={!!field.is_required}
									ticketTypeNames={ticketTypeNames}
									ticketTypeColors={ticketTypeColors}
									helpButton={questionHelp}
									onClick={() => startEdit(String(field.ID))}
								/>
							);
						})}
					</div>
				) : (
					<p className="punchupEmptyState">{t('dashboard-custom-questions-empty', 'No custom questions yet')}</p>
				)
			}
			renderEdit={({ editingId, done }) => <CustomFieldForm customFieldId={editingId} onDone={done} />}
		/>
	);
};
